import React, { useCallback } from 'react';
import { MIN_TIMELINE_ZOOM } from '../constants';
import { useTimelineSize } from './utils/use-timeline-size';
import { useTimelineZoom } from './utils/use-timeline-zoom';
import { ZoomIn, ZoomOut } from '@crabs-ai/web-icons';
import { Slider, ToolButton } from '@crabs-ai/ds-react';
import { useTranslations } from 'next-intl';

export const TimelineZoomSlider: React.FC = () => {
  const t = useTranslations();
  const { zoom, setZoom } = useTimelineZoom();
  const { maxZoom, zoomStep } = useTimelineSize();

  const handleSliderChange = useCallback(
    (value: number) => {
      setZoom(value);
    },
    [setZoom],
  );

  const handleZoomOut = useCallback(() => {
    setZoom((prev) => Math.max(prev - zoomStep, MIN_TIMELINE_ZOOM));
  }, [setZoom, zoomStep]);

  const handleZoomIn = useCallback(() => {
    setZoom((prev) => Math.min(prev + zoomStep, maxZoom));
  }, [setZoom, zoomStep, maxZoom]);

  return (
    <div className="flex items-center gap-3xs">
      <ToolButton
        size="sm"
        state="default"
        onClick={handleZoomOut}
        aria-label="Zoom out timeline"
        isShowTooltip
        tooltipNode={t('Editor.timeline-controls.zoom-out')}
        tooltipSubNode="[⌘/Ctrl+Scroll↓]"
        tooltipPosition="top"
      >
        <ZoomOut className="size-4 [&_path]:fill-sys-cont-neutral-light" />
      </ToolButton>
      <Slider
        value={[zoom]}
        onValueChange={(value) => handleSliderChange(value[0] || 0)}
        step={0.01}
        min={MIN_TIMELINE_ZOOM}
        max={maxZoom}
        direction="horizontal"
        isShowTooltip={true}
        tooltipPosition="top"
        tooltipNode={Number(zoom * 100).toFixed(0) + '%'}
        className={'w-full min-w-[160px]'}
      />
      <ToolButton
        size="sm"
        state="default"
        onClick={handleZoomIn}
        aria-label="Zoom in timeline"
        isShowTooltip
        tooltipNode={t('Editor.timeline-controls.zoom-in')}
        tooltipSubNode="[⌘/Ctrl+Scroll↑]"
        tooltipPosition="top"
      >
        <ZoomIn className="size-4 [&_path]:fill-sys-cont-neutral-light" />
      </ToolButton>
    </div>
  );
};
